import React, { useMemo } from 'react';

import { useColors } from '../../../hooks/lesson-player.context';
import { useSoundContext } from '../../../hooks/use-sound-context';
import type { FontSizeType } from '../../../interfaces/fonts.type';
import { ThemedText } from '../../themed-text';

interface KaraokeTextProps {
  textDurations: number[];
  texts: string[];
  fontSize?: FontSizeType;
}

export function KaraokeText({
  textDurations = [],
  texts = [],
  fontSize = 'small',
}: KaraokeTextProps) {
  const { status } = useSoundContext();
  const colors = useColors();

  const textStartTimes = useMemo(() => {
    const times: number[] = [-2000];
    const visualAidGap = 0;

    for (let i = 1; i < textDurations.length; i++) {
      const accumulate = times[i - 1];
      times[i] = accumulate + (textDurations[i - 1] - visualAidGap);
    }
    return times;
  }, [textDurations]);

  const findActiveIndex = (positionMillis, startTimes) => {
    return startTimes.findIndex(
      (startTime, index) =>
        positionMillis > startTime &&
        (index === startTimes.length - 1 ||
          positionMillis < startTimes[index + 1])
    );
  };

  const textComponents = texts.map((text: string, index: number) => {
    const isFirst = index === 0;

    const activeIndex =
      status.isLoaded && texts.length !== 1
        ? findActiveIndex(status.positionMillis, textStartTimes)
        : -1;
    const isActive = index === activeIndex;

    const startSpace = activeIndex === index && !isFirst ? ' ' : '';
    const endSpace = activeIndex - 1 === index ? '' : ' ';

    return (
      <ThemedText
        key={index}
        type={isActive ? 'primary' : 'gray'}
        fontSize={fontSize}
        style={[
          isActive && {
            backgroundColor: colors.primaryLight,
            color: colors.primary,
          },
        ]}
        isMarkdown={false}
      >
        {startSpace}
        {text?.trim()}
        {endSpace}
      </ThemedText>
    );
  });

  return <>{textComponents}</>;
}
